import type {
  Icd10Coding,
  ProgramEnrollment,
} from "@/features/Charting/RoomOptions/network/useEncounterTransitions";
import type { VisitClosureFormData } from "../schemas";

export type EnrollmentIcd10CodeEntry = {
  code: Icd10Coding;
  sourceKey: string;
};

/**
 * Select the primary + deduped additional ICD-10 codes the clinician just
 * confirmed on the billing summary screen, so display and wire payload cannot diverge.
 */
export const deriveEnrollmentIcd10Codes = (
  data: VisitClosureFormData,
): {
  primaryIcd10Code: Icd10Coding;
  additionalIcd10Codes: EnrollmentIcd10CodeEntry[];
} => {
  const dxBillingCodes = data.dxBillingCodes ?? [];
  const primaryIcd10Code = dxBillingCodes[0] ?? data.icd10.primaryIcd10Code;
  const dxAdditionalEntries: EnrollmentIcd10CodeEntry[] = dxBillingCodes
    .slice(1)
    .map((code) => ({ code, sourceKey: `dx-${code.code}` }));
  const legacyAdditionalEntries: EnrollmentIcd10CodeEntry[] =
    data.icd10.childEncounters.flatMap((child) =>
      (child.icd10Codes ?? []).map((code) => ({
        code,
        sourceKey: `${child.episodeOfCareId}-${code.code}`,
      })),
    );
  const additionalIcd10Codes = (
    dxBillingCodes.length > 0 ? dxAdditionalEntries : legacyAdditionalEntries
  )
    .filter(
      ({ code }) => Boolean(code.code) && code.code !== primaryIcd10Code.code,
    )
    .filter(
      ({ code }, index, entries) =>
        entries.findIndex((candidate) => candidate.code.code === code.code) ===
        index,
    );
  return { primaryIcd10Code, additionalIcd10Codes };
};

/** Assemble the /encounter/close program_enrollment payload from the form. */
export const buildProgramEnrollment = (
  data: VisitClosureFormData,
): ProgramEnrollment | undefined => {
  const { enrollmentApproved } = data.programEnrollment;
  if (enrollmentApproved === null) {
    return undefined;
  }
  const { primaryIcd10Code, additionalIcd10Codes } =
    deriveEnrollmentIcd10Codes(data);
  return {
    enrollmentApproved,
    primaryIcd10Code,
    additionalIcd10Codes: additionalIcd10Codes.map(({ code }) => code),
  };
};
